import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Users, Star, CalendarCheck2, Mail, CheckCircle2, Briefcase,
  Bell, Download, ArrowUpRight, ArrowDownRight, Minus, ChevronRight,
  UserPlus, FileText, Video, X, ChevronDown, Info,
} from 'lucide-react';
import {
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, AreaChart, Area,
} from 'recharts';
import { downloadCompanyAnalyticsPdf } from '../utils/analyticsPdf';
import { MetricDetailModal } from './MetricDetailModal';
import { JOB_BOARDS } from '../constants/jobBoards';

const RANGE_OPTIONS = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
];

const CompanyAnalyticsTab = ({ orgId, companyName }) => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('30d');
  const [positionFilter, setPositionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showPosDropdown, setShowPosDropdown] = useState(false);
  const [activeMetric, setActiveMetric] = useState(null);

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/company/${orgId}/recruiting?range=${range}${positionFilter ? `&positionId=${positionFilter}` : ''}`;
      const r = await axios.get(url);
      setData(r.data?.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [orgId, range, positionFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refresh whenever the tab regains focus so newly created / closed positions
  // and their downstream metrics show up without a manual reload.
  useEffect(() => {
    const onFocus = () => { fetchData(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchData]);

  const rangeLabel = useMemo(() => RANGE_OPTIONS.find(r => r.key === range)?.label || 'Last 30 days', [range]);

  const downloadPdf = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      await downloadCompanyAnalyticsPdf({
        data, companyName, rangeLabel,
        chartElementIds: ['ra-chart-applications-over-time', 'ra-chart-show-up-rate'],
      });
    } finally { setDownloading(false); }
  };

  if (loading || !data) {
    return (
      <div className="p-6" data-testid="company-analytics-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-gray-100 rounded" />
          <div className="grid grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-xl" />)}
          </div>
          <div className="grid grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  const { kpis, rates, needsAttention, funnel, stages, timeSeries, positionPerformance, sourcePerformance, recentActivity, positionOptions } = data;
  const selectedPositionName = positionFilter
    ? (positionOptions.find(p => p.id === positionFilter)?.title || 'Selected position')
    : 'All positions';

  return (
    <div className="p-6" data-testid="company-analytics-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div className="flex-1">
          <h2 className="text-[26px] font-bold text-gray-900 tracking-tight leading-tight">Recruiting analytics</h2>
          <p className="text-sm text-gray-500 mt-1">Live insights from your jobs, applications &amp; interviews.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          <DateRangePicker range={range} onChange={setRange} />
          <PositionDropdown
            open={showPosDropdown}
            setOpen={setShowPosDropdown}
            options={positionOptions}
            value={positionFilter}
            selectedName={selectedPositionName}
            onChange={(v) => { setPositionFilter(v); setShowPosDropdown(false); }}
          />
          <button
            onClick={downloadPdf}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-50 rounded-lg transition"
            data-testid="download-company-analytics-pdf"
          >
            <Download className="w-4 h-4" /> {downloading ? 'Building…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Row 1 — KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <KpiTile
          icon={Users} label="Applications"
          value={kpis.applications.value}
          delta={kpis.applications.delta}
          spark={kpis.applications.spark}
          rangeLabel={rangeLabel}
          testId="kpi-applications"
          onClick={() => setActiveMetric({
            icon: Users, label: 'Applications',
            currentValue: kpis.applications.value,
            series: kpis.applications.spark,
          })}
        />
        <KpiTile
          icon={Star} label="High match candidates (≥80%)"
          value={kpis.highMatch.value}
          sub={`${kpis.highMatch.pctOfApps}% of applications`}
          spark={kpis.highMatch.spark}
          rangeLabel={rangeLabel}
          testId="kpi-high-match"
          sparkColor="#a78bfa"
          onClick={() => setActiveMetric({
            icon: Star, label: 'High match candidates (≥80%)',
            currentValue: kpis.highMatch.value,
            series: kpis.highMatch.spark,
          })}
        />
        <KpiTile
          icon={CalendarCheck2} label="Interviews"
          value={kpis.interviews.value}
          delta={kpis.interviews.delta}
          spark={kpis.interviews.spark}
          rangeLabel={rangeLabel}
          testId="kpi-interviews"
          onClick={() => setActiveMetric({
            icon: CalendarCheck2, label: 'Interviews',
            currentValue: kpis.interviews.value,
            series: kpis.interviews.spark,
          })}
        />
        <KpiTile
          icon={Mail} label="Offers"
          value={kpis.offers.value}
          delta={kpis.offers.delta}
          spark={kpis.offers.spark}
          rangeLabel={rangeLabel}
          testId="kpi-offers"
          onClick={() => setActiveMetric({
            icon: Mail, label: 'Offers',
            currentValue: kpis.offers.value,
            series: kpis.offers.spark,
          })}
        />
        <KpiTile
          icon={CheckCircle2} label="Hires"
          value={kpis.hires.value}
          delta={kpis.hires.delta}
          spark={kpis.hires.spark}
          rangeLabel={rangeLabel}
          testId="kpi-hires"
          onClick={() => setActiveMetric({
            icon: CheckCircle2, label: 'Hires',
            currentValue: kpis.hires.value,
            series: kpis.hires.spark,
          })}
        />
        <KpiTile
          icon={Briefcase} label="Active positions"
          value={kpis.activePositions.value}
          multiSub={[
            kpis.activePositions.noApps > 0 && { color: 'text-amber-600', dot: 'bg-amber-500', text: `${kpis.activePositions.noApps} without applications` },
            kpis.activePositions.idleDays > 0 && { color: 'text-orange-600', dot: 'bg-orange-500', text: `${kpis.activePositions.idleDays} no activity 14+ days` },
          ].filter(Boolean)}
          spark={null}
          testId="kpi-active-positions"
        />
      </div>

      {/* Row 2 — Rate metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <RateTile
          label="Avg. match score"
          value={rates.avgMatchScore.value ? `${rates.avgMatchScore.value}%` : '—'}
          delta={rates.avgMatchScore.delta}
          deltaSuffix="%"
          higherIsBetter
          testId="rate-avg-match"
        />
        <RateTile
          label="Avg. time to first interview"
          value={rates.avgTimeToFirstInterview.value !== null ? `${rates.avgTimeToFirstInterview.value} days` : '—'}
          delta={rates.avgTimeToFirstInterview.delta}
          deltaSuffix=" days"
          higherIsBetter={false}
          testId="rate-time-first-interview"
        />
        <RateTile
          label="Avg. time to offer"
          value={rates.avgTimeToOffer.value !== null ? `${rates.avgTimeToOffer.value} days` : '—'}
          delta={rates.avgTimeToOffer.delta}
          deltaSuffix=" days"
          higherIsBetter={false}
          testId="rate-time-offer"
        />
        <RateTile
          label="Avg. time to hire"
          value={rates.avgTimeToHire.value !== null ? `${rates.avgTimeToHire.value} days` : '—'}
          delta={rates.avgTimeToHire.delta}
          deltaSuffix=" days"
          higherIsBetter={false}
          testId="rate-time-hire"
        />
        <RateTile
          label="Interview show-up rate"
          value={rates.interviewShowUpRate.value !== null ? `${rates.interviewShowUpRate.value}%` : '—'}
          delta={rates.interviewShowUpRate.delta}
          deltaSuffix="%"
          higherIsBetter
          testId="rate-show-up"
        />
        <RateTile
          label="Offer acceptance rate"
          value={rates.offerAcceptanceRate.value !== null ? `${rates.offerAcceptanceRate.value}%` : '—'}
          delta={rates.offerAcceptanceRate.delta}
          deltaSuffix="%"
          higherIsBetter
          testId="rate-offer-accept"
        />
      </div>

      {/* Needs attention */}
      {needsAttention.length > 0 && (
        <NeedsAttentionBar items={needsAttention} />
      )}

      {/* Row 3 — Funnel + Source performance + Position performance */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <FunnelCard funnel={funnel} />
        <SourcePerformanceCard sources={sourcePerformance || []} />
        <PositionPerformanceCard rows={positionPerformance} />
      </div>

      {/* Row 4 — Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <ApplicationsOverTimeCard series={timeSeries} />
        <ShowUpRateCard rates={rates.interviewShowUpRate} rangeLabel={rangeLabel} />
      </div>

      {/* Row 5 — Application stages + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ApplicationStagesCard stages={stages} />
        <RecentActivityCard activity={recentActivity} />
      </div>

      <p className="text-xs text-gray-400 mt-6" data-testid="analytics-footer">
        All data is based on your user permissions and positions. Metrics are calculated in your local time zone.
      </p>

      {/* Metric detail modal */}
      <MetricDetailModal
        isOpen={!!activeMetric}
        onClose={() => setActiveMetric(null)}
        icon={activeMetric?.icon}
        label={activeMetric?.label}
        currentValue={activeMetric?.currentValue}
        series={activeMetric?.series || []}
        granularity={range === 'year' ? 'month' : range === 'quarter' ? 'week' : 'day'}
      />
    </div>
  );
};

/* ─── Date range picker ──────────────────────────────────────────────────── */

const DateRangePicker = ({ range, onChange }) => (
  <div className="inline-flex items-center bg-white border border-gray-200 rounded-lg p-0.5" data-testid="range-picker">
    {RANGE_OPTIONS.map((o) => (
      <button
        key={o.key}
        onClick={() => onChange(o.key)}
        className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
          range === o.key ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-600 hover:text-gray-900'
        }`}
        data-testid={`range-${o.key}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/* ─── Position dropdown ──────────────────────────────────────────────────── */

const PositionDropdown = ({ open, setOpen, options, value, selectedName, onChange }) => (
  <div className="relative" data-testid="position-filter">
    <button
      onClick={() => setOpen(!open)}
      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-lg transition min-w-[180px]"
      data-testid="position-filter-trigger"
    >
      <span className="truncate flex-1 text-left">{selectedName}</span>
      <ChevronDown className="w-4 h-4 text-gray-400" />
    </button>
    {open && (
      <>
        <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
        <div className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto py-1">
          <button
            onClick={() => onChange('')}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${!value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
            data-testid="position-filter-option-all"
          >
            All positions
          </button>
          <div className="h-px bg-gray-100 my-1" />
          {options.map(p => (
            <button
              key={p.id}
              onClick={() => onChange(p.id)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 truncate ${value === p.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
              data-testid={`position-filter-option-${p.id}`}
            >
              {p.title}
            </button>
          ))}
          {options.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-400">No positions yet</p>
          )}
        </div>
      </>
    )}
  </div>
);

/* ─── KPI tile with sparkline ────────────────────────────────────────────── */

const KpiTile = ({ icon: Icon, label, value, delta, sub, multiSub, spark, rangeLabel, testId, sparkColor = '#3b82f6', onClick }) => {
  const deltaDir = delta?.dir;
  const deltaPct = delta?.pct;
  const hasRealDelta = delta && deltaPct !== null && deltaPct !== undefined && deltaPct !== 0;
  const DeltaIcon = deltaDir === 'up' ? ArrowUpRight : deltaDir === 'down' ? ArrowDownRight : Minus;
  const deltaCls = deltaDir === 'up' ? 'text-emerald-600' : deltaDir === 'down' ? 'text-red-500' : 'text-gray-400';
  const gradId = `sg-${testId}`;
  const clickable = typeof onClick === 'function';

  return (
    <div
      className={`bg-white border border-gray-100 rounded-xl p-4 flex flex-col min-h-[180px] transition ${clickable ? 'cursor-pointer hover:border-blue-200 hover:shadow-sm' : ''}`}
      data-testid={testId}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-start gap-2 mb-2 min-h-[36px]">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-50 text-blue-600 flex-shrink-0 mt-0.5">
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="text-[12px] text-gray-600 leading-tight" data-testid={`${testId}-label`}>{label}</span>
      </div>
      <div className="text-[32px] font-semibold text-gray-900 tabular-nums leading-none" data-testid={`${testId}-value`}>
        {value}
      </div>
      <div className="mt-2 min-h-[18px] flex-1">
        {hasRealDelta && (
          <div className="flex items-center flex-wrap gap-x-1 text-[11px] leading-tight">
            <span className={`inline-flex items-center gap-0.5 font-semibold ${deltaCls}`} data-testid={`${testId}-delta`}>
              <DeltaIcon className="w-3 h-3" />
              {deltaPct >= 0 ? '+' : ''}{deltaPct}%
            </span>
            <span className="text-gray-500 whitespace-nowrap">vs {rangeLabel?.toLowerCase()}</span>
          </div>
        )}
        {sub && !multiSub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
        {multiSub && multiSub.length > 0 && (
          <div className="space-y-0.5 mt-0.5">
            {multiSub.map((s, i) => (
              <div key={i} className={`flex items-start gap-1.5 text-[11px] leading-tight ${s.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${s.dot} mt-1 flex-shrink-0`} />
                <span>{s.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {spark && spark.length > 1 && (
        <div className="h-10 mt-2 min-w-[40px]" data-testid={`${testId}-sparkline`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={sparkColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={sparkColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="value" stroke={sparkColor} strokeWidth={1.5} fill={`url(#${gradId})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

/* ─── Rate tile ─────────────────────────────────────────────────────────── */

const RateTile = ({ label, value, delta, deltaSuffix, higherIsBetter, testId }) => {
  let dir = 'flat';
  if (delta !== null && delta !== undefined && delta !== 0) {
    const positive = delta > 0;
    dir = higherIsBetter ? (positive ? 'up' : 'down') : (positive ? 'down' : 'up');
  }
  const DeltaIcon = dir === 'up' ? ArrowUpRight : dir === 'down' ? ArrowDownRight : Minus;
  const deltaCls = dir === 'up' ? 'text-emerald-600' : dir === 'down' ? 'text-red-500' : 'text-gray-400';
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4" data-testid={testId}>
      <div className="text-[12px] text-gray-500 leading-tight mb-3 min-h-[28px]" data-testid={`${testId}-label`}>{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-[26px] font-semibold text-gray-900 tabular-nums leading-none" data-testid={`${testId}-value`}>{value}</span>
        {delta !== null && delta !== undefined && delta !== 0 && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${deltaCls}`} data-testid={`${testId}-delta`}>
            <DeltaIcon className="w-3 h-3" />
            {Math.abs(delta)}{deltaSuffix}
          </span>
        )}
      </div>
    </div>
  );
};

/* ─── Needs attention ───────────────────────────────────────────────────── */

const NeedsAttentionBar = ({ items }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="needs-attention">
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="flex items-start gap-3 lg:border-r lg:border-amber-200 lg:pr-4">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
          <Bell className="w-4 h-4" />
        </span>
        <div>
          <div className="font-semibold text-gray-900 text-sm">Needs attention</div>
          <div className="text-xs text-gray-600">{items.length} item{items.length === 1 ? '' : 's'} need{items.length === 1 ? 's' : ''} your attention</div>
        </div>
      </div>
      {items.slice(0, 3).map((item, i) => (
        <div key={i} className={`${i < 2 ? 'lg:border-r lg:border-amber-200 lg:pr-4' : ''}`} data-testid={`needs-attention-item-${i}`}>
          <div className="font-semibold text-gray-900 text-sm truncate" title={item.title}>{item.title}</div>
          <div className="text-xs text-gray-600 mt-0.5 leading-snug">{item.reason}</div>
          <a href={`/positions/${item.positionId}`} className="inline-flex items-center gap-0.5 text-xs font-medium text-blue-600 hover:text-blue-700 mt-1">
            View position <ChevronRight className="w-3 h-3" />
          </a>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Recruiting funnel ─────────────────────────────────────────────────── */

const FUNNEL_BAR_COLORS = {
  applications: 'bg-blue-500',
  reviewed: 'bg-blue-400',
  shortlisted: 'bg-indigo-400',
  interviews: 'bg-amber-400',
  offers: 'bg-emerald-400',
  hires: 'bg-emerald-500',
};

const FunnelCard = ({ funnel }) => {
  const [showHelp, setShowHelp] = useState(false);
  const max = Math.max(1, ...funnel.map(f => f.value));
  const overall = funnel[0].value > 0 ? Math.round((funnel[funnel.length - 1].value / funnel[0].value) * 100) : 0;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5" data-testid="funnel-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-[15px] flex items-center gap-1.5">
            Recruiting funnel
            <button
              type="button"
              onClick={() => setShowHelp(v => !v)}
              className="text-gray-400 hover:text-gray-700 transition"
              aria-label="How is this funnel calculated?"
              data-testid="funnel-help-toggle"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Conversion rate by stage</p>
        </div>
      </div>
      {showHelp && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-xs text-gray-700 leading-relaxed space-y-1.5" data-testid="funnel-help-panel">
          <p className="font-semibold text-gray-900">How each stage is counted</p>
          <ul className="space-y-1 ml-3 list-disc marker:text-blue-500">
            <li><b>Applications</b> — every candidate who applied inside the selected range.</li>
            <li><b>Reviewed</b> — application status is no longer <i>Pending</i> (someone opened it).</li>
            <li><b>Shortlisted</b> — status is <i>Shortlisted</i> or further, but no interview scheduled yet.</li>
            <li><b>Interviews</b> — at least one interview record exists (async or live) or the candidate is past the Interview stage.</li>
            <li><b>Offers</b> — status is <i>Offered</i> or <i>Accepted</i>.</li>
            <li><b>Hires</b> — status is <i>Accepted</i> (the candidate accepted the offer).</li>
          </ul>
          <p className="pt-1">Numbers only ever decrease down the funnel — each row is a subset of the row above. The <b>%</b> on each row is that stage divided by total <i>Applications</i>.</p>
          <p><b>Overall conversion rate</b> = Hires ÷ Applications, i.e. how many applicants ended up hired.</p>
        </div>
      )}
      <div className="space-y-3">
        {funnel.map(stage => {
          const pctBar = Math.round((stage.value / max) * 100);
          const pctTotal = funnel[0].value > 0 ? Math.round((stage.value / funnel[0].value) * 100) : 0;
          return (
            <div key={stage.key} className="flex items-center gap-3" data-testid={`funnel-row-${stage.key}`}>
              <div className="w-24 text-xs text-gray-700 flex-shrink-0">{stage.label}</div>
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1 h-6 bg-gray-50 rounded overflow-hidden relative">
                  <div className={`h-full ${FUNNEL_BAR_COLORS[stage.key] || 'bg-gray-300'} transition-all rounded`} style={{ width: `${Math.max(3, pctBar)}%` }} />
                  <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-gray-800 tabular-nums">
                    {stage.value}
                  </span>
                </div>
                <div className="w-10 text-xs text-gray-500 text-right tabular-nums">{pctTotal}%</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
        Overall conversion rate: <span className="font-semibold text-gray-900">{overall}%</span>
        <span className="text-gray-400"> — Hires ÷ Applications</span>
      </div>
    </div>
  );
};

/* ─── Position performance ──────────────────────────────────────────────── */

const PositionPerformanceCard = ({ rows }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5" data-testid="position-performance-card">
    <div className="mb-4">
      <h3 className="font-semibold text-gray-900 text-[15px]">Hiring performance by position</h3>
      <p className="text-xs text-gray-500 mt-0.5">Active positions performance overview</p>
    </div>
    {rows.length === 0 ? (
      <p className="text-sm text-gray-400 text-center py-8">No activity yet for this range.</p>
    ) : (
      <div className="overflow-hidden">
        <div className="grid grid-cols-[minmax(0,1fr)_44px_44px_36px_10px] gap-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-2 border-b border-gray-100">
          <span>Position</span>
          <span className="text-right">Apps</span>
          <span className="text-right">Intvw</span>
          <span className="text-right">Hires</span>
          <span />
        </div>
        <div className="divide-y divide-gray-50">
          {rows.map(r => {
            const dot = r.hires > 0 ? 'bg-emerald-500' : r.interviews > 0 ? 'bg-blue-500' : r.applications > 0 ? 'bg-amber-500' : 'bg-gray-300';
            return (
              <div key={r.id} className="grid grid-cols-[minmax(0,1fr)_44px_44px_36px_10px] gap-3 py-2.5 items-center text-sm" data-testid={`position-row-${r.id}`}>
                <span className="text-gray-800 truncate" title={r.title}>{r.title}</span>
                <span className="text-right tabular-nums text-gray-800">{r.applications}</span>
                <span className="text-right tabular-nums text-gray-800">{r.interviews}</span>
                <span className="text-right tabular-nums text-gray-800">{r.hires}</span>
                <span className={`w-2 h-2 rounded-full ${dot} justify-self-center`} />
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

/* ─── Applications over time ────────────────────────────────────────────── */

const ApplicationsOverTimeCard = ({ series }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5" data-testid="applications-over-time-card">
    <div className="mb-4">
      <h3 className="font-semibold text-gray-900 text-[15px]">Applications over time</h3>
      <p className="text-xs text-gray-500 mt-0.5">Applications, interviews and hires over time</p>
    </div>
    <div id="ra-chart-applications-over-time" data-pdf-title="Applications over time" className="h-64" data-testid="chart-applications-over-time">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
          <Line type="monotone" dataKey="Applications" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2.5, fill: '#3b82f6' }} />
          <Line type="monotone" dataKey="Interviews" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2.5, fill: '#a78bfa' }} />
          <Line type="monotone" dataKey="Hires" stroke="#10b981" strokeWidth={2} dot={{ r: 2.5, fill: '#10b981' }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

/* ─── Show-up rate donut ────────────────────────────────────────────────── */

const ShowUpRateCard = ({ rates, rangeLabel }) => {
  const completed = rates.completed || 0;
  const noShow = rates.noShow || 0;
  const total = completed + noShow;
  const showRate = rates.value;
  const data = [
    { name: 'Completed', value: completed, color: '#3b82f6' },
    { name: 'No-show', value: noShow, color: '#f97316' },
  ];
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5" data-testid="show-up-rate-card">
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-[15px]">Interview show-up rate</h3>
        <p className="text-xs text-gray-500 mt-0.5">Completed vs no-show</p>
      </div>
      <div className="flex items-center gap-6">
        <div id="ra-chart-show-up-rate" data-pdf-title="Interview show-up rate" className="relative w-40 h-40 flex-shrink-0" data-testid="chart-show-up-rate">
          {total > 0 ? (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%" cy="50%" innerRadius={55} outerRadius={72}
                    paddingAngle={2} dataKey="value" stroke="none"
                  >
                    {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900 tabular-nums">{showRate !== null ? `${showRate}%` : '—'}</span>
                <span className="text-[11px] text-gray-500">Show-up rate</span>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-full">
              <span className="text-lg font-semibold text-gray-400">—</span>
              <span className="text-[10px] text-gray-400">No data</span>
            </div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <span className="text-gray-600">Completed</span>
            <span className="ml-auto tabular-nums text-gray-800 font-semibold">
              {total > 0 ? `${Math.round((completed / total) * 100)}% (${completed})` : '—'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
            <span className="text-gray-600">No-show</span>
            <span className="ml-auto tabular-nums text-gray-800 font-semibold">
              {total > 0 ? `${Math.round((noShow / total) * 100)}% (${noShow})` : '—'}
            </span>
          </div>
          <div className="pt-3 mt-3 border-t border-gray-100 flex items-center gap-2 text-xs">
            <span className="text-gray-500">vs {rangeLabel?.toLowerCase()}</span>
            {rates.delta !== null && rates.delta !== 0 && (
              <span className={`inline-flex items-center gap-0.5 font-semibold ${rates.delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {rates.delta > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(rates.delta)}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Application stages ────────────────────────────────────────────────── */

const STAGE_DOT_COLORS = {
  applications: 'bg-gray-400',
  reviewed: 'bg-blue-500',
  shortlisted: 'bg-indigo-500',
  interviews: 'bg-amber-500',
  offers: 'bg-emerald-500',
  hires: 'bg-emerald-600',
};

const ApplicationStagesCard = ({ stages }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5" data-testid="application-stages-card">
    <div className="mb-4">
      <h3 className="font-semibold text-gray-900 text-[15px]">Application stages</h3>
      <p className="text-xs text-gray-500 mt-0.5">Detailed breakdown</p>
    </div>
    <div className="grid grid-cols-[minmax(0,1fr)_64px_88px] gap-4 text-[11px] font-semibold text-gray-500 uppercase tracking-wide pb-2 border-b border-gray-100">
      <span>Stage</span>
      <span className="text-right">Count</span>
      <span className="text-right">% of total</span>
    </div>
    <div className="divide-y divide-gray-50">
      {stages.map(s => (
        <div key={s.key} className="grid grid-cols-[minmax(0,1fr)_64px_88px] gap-4 py-2.5 items-center text-sm" data-testid={`stage-row-${s.key}`}>
          <span className="flex items-center gap-2 text-gray-800 min-w-0">
            <span className={`w-2 h-2 rounded-full ${STAGE_DOT_COLORS[s.key] || 'bg-gray-300'} flex-shrink-0`} />
            <span className="truncate">{s.label}</span>
          </span>
          <span className="text-right tabular-nums text-gray-800">{s.value}</span>
          <span className="text-right tabular-nums text-gray-500">{s.pctTotal}%</span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Source performance ────────────────────────────────────────────────── */

const jobBoardMeta = (key) => {
  if (key === 'company_website') return { name: 'Company Website', logoUrl: null, color: '#3b82f6' };
  const meta = JOB_BOARDS.find(b => b.id === key);
  if (meta) return { name: meta.name, logoUrl: meta.logoUrl, color: meta.color };
  return { name: key, logoUrl: null, color: '#94a3b8' };
};

const SourcePerformanceCard = ({ sources }) => {
  const hasEstimated = sources.some(s => s.attributionEstimate);
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5" data-testid="source-performance-card">
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-[15px]">Source performance</h3>
        <p className="text-xs text-gray-500 mt-0.5">Where your candidates come from</p>
      </div>
      {sources.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No sources tracked yet.</p>
      ) : (
        <div className="overflow-hidden">
          <div className="grid grid-cols-[minmax(0,1fr)_44px_44px_36px_44px] gap-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-2 border-b border-gray-100">
            <span>Source</span>
            <span className="text-right">Apps</span>
            <span className="text-right">Intvw</span>
            <span className="text-right">Hires</span>
            <span className="text-right">Rate</span>
          </div>
          <div className="divide-y divide-gray-50">
            {sources.map(s => {
              const meta = jobBoardMeta(s.key);
              return (
                <div key={s.key} className="grid grid-cols-[minmax(0,1fr)_44px_44px_36px_44px] gap-3 py-2.5 items-center text-sm" data-testid={`source-row-${s.key}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    {meta.logoUrl ? (
                      <img src={meta.logoUrl} alt="" className="w-4 h-4 flex-shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <span className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: meta.color, opacity: 0.9 }} />
                    )}
                    <span className="truncate text-gray-800" title={meta.name}>{meta.name}</span>
                    {s.attributionEstimate && (
                      <span className="text-[10px] text-gray-400" title="Estimated via position exposure — per-application attribution coming with the source tracking pixel.">~</span>
                    )}
                  </span>
                  <span className="text-right tabular-nums text-gray-800">{s.applications}</span>
                  <span className="text-right tabular-nums text-gray-800">{s.interviews}</span>
                  <span className="text-right tabular-nums text-gray-800">{s.hires}</span>
                  <span className="text-right tabular-nums text-gray-500">{s.hireRate}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {hasEstimated && (
        <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
          <span className="font-semibold">~</span> Estimated. Each row reflects candidates on positions published to that board — per-application attribution arrives with the source-tracking pixel.
        </p>
      )}
    </div>
  );
};

/* ─── Recent activity ───────────────────────────────────────────────────── */

const ACTION_ICON = {
  position_created: Briefcase,
  position_published: Briefcase,
  position_closed: X,
  video_invite_sent: Video,
  async_invite_sent: Video,
  contract_email_sent: FileText,
  rejection_email_sent: X,
  recruiter_invited: UserPlus,
  recruiter_joined: UserPlus,
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / (1000 * 60));
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

/** Renders a single recent-activity row as two lines: a rich headline
 * ("Interview invitation sent to Anna Becker") plus a subline with the actor
 * ("by Sarah Johnson"). Falls back gracefully when data is missing. */
const describeActivity = (a) => {
  const role = a.roleTitle || 'a role';
  const candidate = a.candidateName || 'candidate';
  switch (a.action) {
    case 'position_created':
      return { title: <>New role created — <span className="font-medium text-gray-900">{role}</span></> };
    case 'position_published':
      return { title: <>Role published — <span className="font-medium text-gray-900">{role}</span></> };
    case 'position_closed':
      return { title: <>Role closed — <span className="font-medium text-gray-900">{role}</span></> };
    case 'video_invite_sent':
      return { title: <>Live interview invite sent to <span className="font-medium text-gray-900">{candidate}</span>{a.roleTitle ? <> for <span className="font-medium text-gray-900">{a.roleTitle}</span></> : null}</> };
    case 'async_invite_sent':
      return { title: <>Video interview invite sent to <span className="font-medium text-gray-900">{candidate}</span>{a.roleTitle ? <> for <span className="font-medium text-gray-900">{a.roleTitle}</span></> : null}</> };
    case 'contract_email_sent':
      return { title: <>Contract email sent to <span className="font-medium text-gray-900">{candidate}</span>{a.roleTitle ? <> for <span className="font-medium text-gray-900">{a.roleTitle}</span></> : null}</> };
    case 'rejection_email_sent':
      return { title: <>Rejection email sent to <span className="font-medium text-gray-900">{candidate}</span></> };
    case 'recruiter_invited':
      return { title: <>Recruiter invited</> };
    case 'recruiter_joined':
      return { title: <>Recruiter joined the team</> };
    default:
      return { title: <>{a.action.replace(/_/g, ' ')}</> };
  }
};

const RecentActivityCard = ({ activity }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5" data-testid="recent-activity-card">
    <div className="mb-4 flex items-baseline justify-between gap-3">
      <div>
        <h3 className="font-semibold text-gray-900 text-[15px]">Recent activity</h3>
        <p className="text-xs text-gray-500 mt-0.5">Latest updates across your hiring · not date-range filtered</p>
      </div>
      {activity.length > 0 && (
        <span className="text-xs text-gray-400 tabular-nums" data-testid="recent-activity-count">
          {activity.length}
        </span>
      )}
    </div>
    {activity.length === 0 ? (
      <p className="text-sm text-gray-400 text-center py-8">No activity yet.</p>
    ) : (
      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1 -mr-1" data-testid="recent-activity-scroll">
        {activity.map(a => {
          const Icon = ACTION_ICON[a.action] || FileText;
          const { title } = describeActivity(a);
          return (
            <div key={a.id} className="flex items-start gap-3" data-testid={`activity-${a.id}`}>
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex-shrink-0 mt-0.5">
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-800 leading-snug">{title}</div>
                {a.actorName && (
                  <div className="text-xs text-gray-500 mt-0.5">by {a.actorName}</div>
                )}
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap">{formatRelative(a.createdAt)}</span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default CompanyAnalyticsTab;
